/**
 * @typedef {Object} KlaviyoLineItem
 * @property {string|null} title
 * @property {string|null} variant_title
 * @property {string|null} sku
 * @property {string|null} product_url
 * @property {string|null} image_url
 * @property {number|string|null} value
 * @property {number|string|null} price
 * @property {string|null} currency
 * @property {Record<string, unknown>|null} inventory
 */

/**
 * @typedef {Object} KlaviyoOrderMetadata
 * @property {string|null} order_id
 * @property {string|null} order_name
 * @property {number|string|null} item_count
 */

/**
 * @typedef {Object} ShopifyEnrichmentMatch
 * @property {boolean} matched
 * @property {string|null} match_status
 * @property {Record<string, unknown>|null} product
 * @property {Record<string, unknown>|null} variant
 * @property {ShopifyEnrichmentMatch[]} line_items
 */

/**
 * @typedef {Object} KlaviyoEventMetadata
 * @property {'product'|'order'|string|null} commerce_object
 * @property {KlaviyoLineItem|null} product
 * @property {KlaviyoLineItem|null} primary_line_item
 * @property {KlaviyoLineItem[]} line_items
 * @property {ShopifyEnrichmentMatch|null} shopify_enrichment
 * @property {KlaviyoOrderMetadata|null} order
 * @property {number|string|null} line_item_count
 * @property {number|string|null} value
 * @property {number|string|null} price
 * @property {string|null} currency
 */

/**
 * @typedef {Object} KlaviyoEvent
 * @property {number|string} id
 * @property {string|null} event_name
 * @property {string|null} metric_name
 * @property {string|null} occurred_at
 * @property {string|number|null} value
 * @property {KlaviyoEventMetadata|Record<string, unknown>|null} metadata
 */

const DISPLAY_IMAGE_WIDTH = 200;

/**
 * Checks whether a value can be safely read as an object.
 *
 * @param {unknown} value Provider-controlled payload value.
 * @returns {value is Record<string, unknown>}
 */
export const isRecord = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * Returns event metadata as a safe object.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {Record<string, unknown>}
 */
export const eventMetadata = event =>
  isRecord(event?.metadata) ? event.metadata : {};

/**
 * Returns a non-empty string value.
 *
 * @param {unknown} value Provider-controlled payload value.
 * @returns {string|null}
 */
export const stringValue = value =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/**
 * Reads a non-empty string field from an object.
 *
 * @param {Record<string, unknown>} source Object to read.
 * @param {string} key Field key.
 * @returns {string|null}
 */
export const stringField = (source, key) => stringValue(source?.[key]);

/**
 * Returns a browser-safe absolute HTTP(S) URL.
 *
 * @param {unknown} value Provider-controlled URL value.
 * @returns {string|null}
 */
const httpUrlValue = value => {
  const url = stringValue(value);
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);
    return ['http:', 'https:'].includes(parsedUrl.protocol) ? url : null;
  } catch {
    return null;
  }
};

/**
 * Reads a safe absolute HTTP(S) URL field from an object.
 *
 * @param {Record<string, unknown>} source Object to read.
 * @param {string} key Field key.
 * @returns {string|null}
 */
const httpUrlField = (source, key) => httpUrlValue(source?.[key]);

/**
 * Adds a display width hint to safe product image URLs.
 *
 * @param {string|null} imageUrl Product image URL.
 * @returns {string|null} Product image URL constrained for UI display.
 */
const displayImageUrl = imageUrl => {
  if (!imageUrl) return null;

  const parsedUrl = new URL(imageUrl);
  parsedUrl.searchParams.set('width', String(DISPLAY_IMAGE_WIDTH));
  return parsedUrl.toString();
};

/**
 * Returns a numeric or string amount suitable for display.
 *
 * @param {unknown} value Provider-controlled payload value.
 * @returns {number|string|null}
 */
export const amountValue = value =>
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'string' && value.trim())
    ? value
    : null;

/**
 * Reads matched Shopify enrichment metadata when available.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {Record<string, unknown>|null}
 */
export function eventShopifyEnrichment(event) {
  const enrichment = eventMetadata(event).shopify_enrichment;
  if (!isRecord(enrichment)) return null;
  if (enrichment.matched !== true || enrichment.match_status !== 'matched') {
    return null;
  }

  return enrichment;
}

/**
 * Converts one Shopify enrichment match into the line-item shape used by cards.
 *
 * @param {Record<string, unknown>|null} enrichment Shopify enrichment match.
 * @returns {Record<string, unknown>|null}
 */
function enrichedLineItem(enrichment) {
  if (!isRecord(enrichment)) return null;

  const product = isRecord(enrichment.product) ? enrichment.product : {};
  const variant = isRecord(enrichment.variant) ? enrichment.variant : {};
  const title = stringField(product, 'title');

  return {
    title,
    product_title: title,
    variant_title: stringField(variant, 'title'),
    sku: stringField(variant, 'sku'),
    barcode: stringField(variant, 'barcode'),
    product_url: httpUrlField(product, 'online_store_url'),
    image_url:
      httpUrlField(variant, 'image_url') || httpUrlField(product, 'image_url'),
    value: amountValue(variant.price),
    price: amountValue(variant.price),
    currency: stringField(variant, 'currency'),
    inventory: isRecord(enrichment.inventory) ? enrichment.inventory : null,
  };
}

/**
 * Returns the best matched Shopify line item for display.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {Record<string, unknown>|null}
 */
function eventEnrichedPrimaryLineItem(event) {
  const enrichment = eventShopifyEnrichment(event);
  if (!enrichment) return null;

  if (Array.isArray(enrichment.line_items)) {
    const matchedLineItem =
      enrichment.line_items.find(item => item?.matched === true) ||
      enrichment.line_items[0];
    return enrichedLineItem(matchedLineItem);
  }

  return enrichedLineItem(enrichment);
}

/**
 * Returns the primary line item for product preview purposes.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {Record<string, unknown>}
 */
export function eventPrimaryLineItem(event) {
  const enrichedItem = eventEnrichedPrimaryLineItem(event);
  if (enrichedItem) return enrichedItem;

  const metadata = eventMetadata(event);
  if (isRecord(metadata.primary_line_item)) return metadata.primary_line_item;
  if (isRecord(metadata.product)) return metadata.product;
  if (Array.isArray(metadata.line_items) && isRecord(metadata.line_items[0])) {
    return metadata.line_items[0];
  }

  return metadata;
}

/**
 * Returns order metadata for order-level events.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {Record<string, unknown>}
 */
export function eventOrder(event) {
  const metadata = eventMetadata(event);
  return isRecord(metadata.order) ? metadata.order : metadata;
}

/**
 * Returns the canonical display name for a parsed CRM event.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @param {string} fallback Fallback event name.
 * @returns {string}
 */
export const eventDisplayName = (event, fallback) =>
  event?.event_name || event?.metric_name || fallback;

/**
 * Builds a compact commerce summary from normalized metadata.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @param {Object} options Summary options.
 * @param {string|null} options.itemCountLabel Localized order item count label.
 * @param {string|null} options.inventoryLabel Localized inventory availability label.
 * @returns {string}
 */
export function eventMetadataSummary(
  event,
  { itemCountLabel = null, inventoryLabel = null } = {}
) {
  const item = eventPrimaryLineItem(event);
  const order = eventOrder(event);
  return [
    stringField(order, 'order_name') || stringField(order, 'order_id'),
    stringField(item, 'title') || stringField(item, 'product_title'),
    stringField(item, 'variant_title'),
    stringField(item, 'sku'),
    inventoryLabel,
    itemCountLabel,
  ]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .slice(0, 5)
    .join(' / ');
}

/**
 * Builds a localized inventory availability label for matched Shopify enrichment.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @param {Object} options Inventory label callbacks.
 * @param {Function|null} options.availableLabel Formats a total availability label.
 * @param {Function|null} options.availableAtLocationLabel Formats one-location availability.
 * @param {Function|null} options.availableAtLocationsLabel Formats multi-location availability.
 * @returns {string|null}
 */
export function eventInventoryLabel(
  event,
  {
    availableLabel = null,
    availableAtLocationLabel = null,
    availableAtLocationsLabel = null,
  } = {}
) {
  const inventory = eventPrimaryLineItem(event).inventory;
  if (!isRecord(inventory)) return null;

  const totalAvailable = amountValue(inventory.total_available);
  if (totalAvailable === null) return null;

  const locations = Array.isArray(inventory.locations)
    ? inventory.locations.filter(isRecord)
    : [];
  const locationNames = locations
    .map(location => stringField(location, 'name'))
    .filter(Boolean);
  const primaryLocationName = locationNames[0];

  if (locationNames.length === 1 && availableAtLocationLabel) {
    return availableAtLocationLabel({
      count: totalAvailable,
      location: primaryLocationName,
    });
  }

  if (locationNames.length > 1 && availableAtLocationsLabel) {
    return availableAtLocationsLabel({
      count: totalAvailable,
      locationCount: locationNames.length,
    });
  }

  if (availableLabel) {
    return availableLabel({ count: totalAvailable });
  }

  return String(totalAvailable);
}

/**
 * Returns the most useful value label for commerce events.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {string|number|null}
 */
export function eventValueLabel(event) {
  const metadata = eventMetadata(event);
  const item = eventPrimaryLineItem(event);
  const amount =
    amountValue(event?.value) ||
    amountValue(metadata.value) ||
    amountValue(item.value) ||
    amountValue(item.price);
  if (!amount) return null;

  const currency =
    stringField(metadata, 'currency') || stringField(item, 'currency');
  return currency ? `${amount} ${currency}` : amount;
}

/**
 * Returns the product detail URL for a parsed event when supplied.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {string|null}
 */
export const eventProductUrl = event =>
  httpUrlField(eventPrimaryLineItem(event), 'product_url');

/**
 * Returns the product image URL for a parsed event when supplied.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {string|null}
 */
export const eventProductImageUrl = event =>
  displayImageUrl(httpUrlField(eventPrimaryLineItem(event), 'image_url'));

/**
 * Returns the accessible image label for a parsed event.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @param {string} fallback Fallback event name.
 * @returns {string}
 */
export const eventProductImageAlt = (event, fallback) =>
  stringField(eventPrimaryLineItem(event), 'title') ||
  stringField(eventPrimaryLineItem(event), 'product_title') ||
  eventDisplayName(event, fallback);

/**
 * Returns a line item count for order-level events.
 *
 * @param {KlaviyoEvent} event Parsed CRM event.
 * @returns {number|null}
 */
export function eventLineItemCount(event) {
  const metadata = eventMetadata(event);
  const rawCount = metadata.line_item_count || eventOrder(event).item_count;
  if (typeof rawCount === 'number' && Number.isFinite(rawCount))
    return rawCount;
  if (typeof rawCount === 'string' && rawCount.trim()) {
    const parsed = Number(rawCount);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (Array.isArray(metadata.line_items) && metadata.line_items.length) {
    return metadata.line_items.length;
  }
  return null;
}
